import PropTypes from 'prop-types';
import React from 'react';
import { CheckIcon } from '@prisjakt/ui-icons';
import Checkbox from './checkbox';
import * as S from './styles';

const generateFakeId = (parents, child) =>
  [child, ...parents].map(x => x.name).join('/');

const isSelected = (selected, child) => {
  if (selected === true) return selected;

  if (selected.includes(child.id)) {
    return true;
  }

  if (child.children && child.children.length) {
    return child.children.reduce(isSelected, selected);
  }

  return selected;
};

function hasSelectedChild(selected, children) {
  if (!children || !selected.length) return false;

  return children.reduce(isSelected, selected) === true;
}

function hasSelectedAllChildren(selected, children) {
  if (!children || !children.length || !selected.length) return false;

  const allSelected = children.filter(child => selected.includes(child.id));

  return allSelected.length === children.length;
}

const List = props => {
  const {
    id,
    values,
    selected,
    selectedParent,
    onChange,
    parents,
    onExpand,
    expanded,
    autoSelectChildren,
    layout,
  } = props;

  return (
    <S.Layout layout={layout}>
      {values.map(value => {
        // Not all options have an id, so we have to generate one that we can use as an identifier
        const child = {
          ...value,
          id: value.id || generateFakeId(parents, value),
        };

        const isSelectable = child.selectable !== false;
        let isChecked =
          isSelectable &&
          ((selectedParent && autoSelectChildren) ||
            selected.includes(child.id) ||
            selected.includes(parseInt(child.id, 10)) ||
            hasSelectedAllChildren(selected, child.children));
        const isPartiallyChecked =
          isSelectable &&
          hasSelectedChild(selected, child.children) &&
          !hasSelectedAllChildren(selected, child.children);
        const isExpanded = expanded.includes(child.id);

        // Prevent the box from being checked if it already is partially checked and shouldn't select children
        if (!autoSelectChildren && isPartiallyChecked) {
          isChecked = false;
        }

        const nonSelectableParentProps = {
          as: 'span',
          onClick: e => onExpand(e, child.id),
        };

        return (
          <S.ListItem key={child.id} selected={isChecked || isPartiallyChecked}>
            <S.ListLabel {...(isSelectable ? {} : nonSelectableParentProps)}>
              {isSelectable && (
                <React.Fragment>
                  <Checkbox child={child} isChecked={isChecked} {...props} />

                  <S.Checkbox checked={isChecked} partial={isPartiallyChecked}>
                    <CheckIcon
                      styling="neutral-light"
                      strokeWidth="6"
                      height={14}
                      left={1}
                      top={1}
                      checked={isChecked}
                      partial={isPartiallyChecked}
                    />
                  </S.Checkbox>
                </React.Fragment>
              )}

              <S.ListLabelText>
                <S.ListLabelName>{child.name}</S.ListLabelName>
                <S.ListLabelByline>({child.products})</S.ListLabelByline>
              </S.ListLabelText>

              {child.children && Boolean(child.children.length) && (
                <S.ExpandWrapper>
                  <S.ExpandButton
                    as={isSelectable ? undefined : 'span'}
                    expanded={isExpanded}
                    outlined={!isExpanded}
                    onClick={
                      isSelectable ? e => onExpand(e, child.id) : undefined
                    }
                  >
                    {isExpanded ? <S.ArrowUp /> : <S.ArrowDown />}
                  </S.ExpandButton>
                </S.ExpandWrapper>
              )}
            </S.ListLabel>

            {child.children && isExpanded && (
              <List
                id={id}
                values={child.children}
                parents={[child, ...parents]}
                onChange={onChange}
                selected={selected}
                selectedParent={isChecked}
                expanded={expanded}
                onExpand={onExpand}
                autoSelectChildren={autoSelectChildren}
              />
            )}
          </S.ListItem>
        );
      })}
    </S.Layout>
  );
};

const {
  oneOfType,
  arrayOf,
  string,
  bool,
  shape,
  func,
  number,
  oneOf,
} = PropTypes;

const valueType = {
  id: string.isRequired,
  name: string.isRequired,
  products: number,
};

valueType.children = arrayOf(shape(valueType));

List.propTypes = {
  id: oneOfType([number, string]).isRequired,
  values: arrayOf(shape(valueType)).isRequired,
  selectedParent: bool,
  selected: arrayOf(string),
  parents: arrayOf(shape(valueType)),
  onChange: func,
  onExpand: func,
  expanded: arrayOf(string),
  autoSelectChildren: bool,
  layout: oneOf(['vertical', 'horizontal']),
};

List.defaultProps = {
  parents: [],
  onChange: () => {},
  selected: [],
  selectedParent: false,
  onExpand: () => {},
  expanded: [],
  autoSelectChildren: true,
  layout: 'vertical',
};

export default List;
